// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NewStep04Housing - Wizard stap 4: Koopwoning
 * V35-P2c: Checkbox UX - woonadres standaard overgenomen, velden verborgen.
 */
import { useState, useEffect, useCallback } from 'react'
import { Home, Check } from 'lucide-react'
import { InfoBlock }  from '@/components/ui/InfoBlock'
import { Input }      from '@/components/ui/Input'
import { Card }       from '@/components/ui/Card'
import { useWizardStore, useAuthStore } from '@/store'
import { useAutosave } from '@/hooks/useAutosave'
import { loadHousing, saveHousing, EMPTY_HOUSING, type HousingData, wizardLoad, ORV_INSURERS } from '@/services/wizard'
import { SelectWithOther } from '@/components/ui/SelectWithOther'
import { getProfileAddress } from '@/services/profile'
import type { SaveStatus } from '@/hooks/useAutosave'

function buildAddress(
  street: string, houseNumber: string, addition: string,
  postalCode: string, city: string,
): string {
  const line1 = [street, houseNumber, addition].filter(Boolean).join(' ')
  const line2 = [postalCode, city].filter(Boolean).join(' ')
  return [line1, line2].filter(Boolean).join(', ')
}

export function NewStep04Housing({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { answers, setAnswer } = useWizardStore()
  const { userId }             = useAuthStore()
  const [data,       setData]  = useState<HousingData>(EMPTY_HOUSING)
  const [ready,      setReady] = useState(false)

  // Adresdata uit Persoonlijke gegevens (wizard_personal of encryptedProfile)
  const [profileStreet,   setProfileStreet]   = useState('')
  const [profileNumber,   setProfileNumber]   = useState('')
  const [profileAddition, setProfileAddition] = useState('')
  const [profilePostal,   setProfilePostal]   = useState('')
  const [profileCity,     setProfileCity]     = useState('')

  const hasOwnHome        = answers.hasOwnHome
  const hasProfileAddress = !!(profileStreet || profilePostal || profileCity)

  // useProfileAddress='yes' = checkbox aangevinkt (woonadres overnemen)
  // useProfileAddress='no'  = checkbox uitgevinkt (eigen adres)
  const useProfile = data.useProfileAddress !== 'no'

  useEffect(() => {
    if (!userId) return
    onSaveStatus('idle')

    async function load() {
      // 1. Laad adres uit wizard_personal; fallback naar encryptedProfile
      let s = '', n = '', a = '', pc = '', c = ''

      const personal = await wizardLoad<{
        street?: string; houseNumber?: string; houseNumberAddition?: string
        postalCode?: string; city?: string
      }>(userId!, 'wizard_personal')

      if (personal && (personal.street || personal.postalCode || personal.city)) {
        s  = personal.street              ?? ''
        n  = personal.houseNumber         ?? ''
        a  = personal.houseNumberAddition ?? ''
        pc = personal.postalCode          ?? ''
        c  = personal.city                ?? ''
      } else {
        // wizard_personal leeg of niet aanwezig → lees rechtstreeks uit profiel
        const addr = await getProfileAddress(userId!)
        if (addr) { s = addr.street; n = addr.houseNumber; a = addr.houseNumberAddition; pc = addr.postalCode; c = addr.city }
      }

      setProfileStreet(s); setProfileNumber(n); setProfileAddition(a)
      setProfilePostal(pc); setProfileCity(c)

      // 2. Laad opgeslagen housing
      const saved = await loadHousing(userId!)

      if (saved) {
        const usesProfile = saved.useProfileAddress !== 'no'
        if (usesProfile && (s || pc)) {
          // Altijd huidige profielwaarden tonen - nooit stale opgeslagen waarden
          setData({
            ...EMPTY_HOUSING, ...saved,
            street: s, houseNumber: n, houseNumberAddition: a,
            postalCode: pc, city: c,
            useProfileAddress: 'yes',
            address: buildAddress(s, n, a, pc, c),
          })
        } else {
          setData({ ...EMPTY_HOUSING, ...saved })
        }
      } else if (s || pc) {
        // Eerste bezoek + profiel heeft adres → prefill + checkbox aangevinkt
        setData({
          ...EMPTY_HOUSING,
          street: s, houseNumber: n, houseNumberAddition: a,
          postalCode: pc, city: c,
          useProfileAddress: 'yes',
          address: buildAddress(s, n, a, pc, c),
        })
      }
      setReady(true)
    }

    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const save = useCallback(async (d: HousingData) => {
    if (userId) await saveHousing(userId, d)
  }, [userId])
  const { status } = useAutosave({ data, onSave: save, enabled: ready && hasOwnHome === true })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function setHas(v: string) {
    setAnswer('hasOwnHome', v === 'yes')
  }

  function toggleUseProfile() {
    if (useProfile) {
      // Uitvinken → eigen adresvelden leegmaken zodat gebruiker vers kan invoeren
      setData(p => ({ ...p, useProfileAddress: 'no', street: '', houseNumber: '', houseNumberAddition: '', postalCode: '', city: '', address: '' }))
    } else {
      // Aanvinken → profiel-adres terugzetten
      setData(p => ({
        ...p,
        street: profileStreet, houseNumber: profileNumber, houseNumberAddition: profileAddition,
        postalCode: profilePostal, city: profileCity,
        useProfileAddress: 'yes',
        address: buildAddress(profileStreet, profileNumber, profileAddition, profilePostal, profileCity),
      }))
    }
  }

  function updAddr(field: keyof HousingData, value: string) {
    setData(p => {
      const updated = { ...p, [field]: value, useProfileAddress: 'no' }
      updated.address = buildAddress(updated.street, updated.houseNumber, updated.houseNumberAddition, updated.postalCode, updated.city)
      return updated
    })
  }

  // Adresregels voor display (profiel-adres, nooit data-adres in readonly modus)
  const displayStreet   = profileStreet   || data.street
  const displayNumber   = profileNumber   || data.houseNumber
  const displayAddition = profileAddition || data.houseNumberAddition
  const displayPostal   = profilePostal   || data.postalCode
  const displayCity     = profileCity     || data.city

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Koopwoning</h2>
        <p className="text-sm text-ink-3">Uw eigen woning - met of zonder hypotheek.</p>
      </div>

      <InfoBlock title="Goed om te weten - koopwoning" variant="blue" defaultOpen={false}>
        <p>Een woning brengt meer met zich mee dan alleen een hypotheek. Er zijn verzekeringen, sleutels, contracten en vaak vaste lasten die gewoon doorlopen.</p>
        <p>Regelmatig ontdekken families pas na een paar weken dat de hypotheek niet meer is betaald, omdat de rekening op één naam was geblokkeerd. Noteer daarom het <strong>hypotheeknummer</strong>; daarmee vinden uw naasten de hypotheek direct terug, sneller dan met alleen de naam van de bank.</p>
        <p>Hangt er een <strong>overlijdensrisicoverzekering</strong> aan de hypotheek, dan kan die een deel van de schuld aflossen, maar alleen als iemand weet dat hij bestaat. Leg ook vast hoe de <strong>eigendomssituatie</strong> is: op uw naam, samen met uw partner, of via een andere constructie. En denk aan de kleine dingen die later veel tijd kosten: waar liggen de reservesleutels van de voordeur, de meterkast en de garage, en de code van het alarm?</p>
                                <p className="mt-2">
          <a href="https://www.kadaster.nl/" target="_blank" rel="noopener noreferrer"
             className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
            Kadaster.nl - eigendomsinformatie van uw woning opzoeken →
          </a>
        </p>
        <p className="text-xs text-ink-4 mt-2 pt-2 border-t border-sand-200 leading-relaxed">
          WilsApp legt uw wensen vast als persoonlijke leidraad. Voor officiële registratie of financieel advies verwijzen wij naar de bevoegde instantie.
        </p>
      </InfoBlock>

      {/* Ja / Nee */}
      <div className="flex flex-col gap-2">
        {[{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }].map(opt => (
          <button key={opt.value} type="button" onClick={() => setHas(opt.value)}
            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${
              (hasOwnHome === true && opt.value === 'yes') || (hasOwnHome === false && opt.value === 'no')
                ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
            }`}>
            <div className={`h-5 w-5 rounded-full border-2 flex-shrink-0 ${
              (hasOwnHome === true && opt.value === 'yes') || (hasOwnHome === false && opt.value === 'no')
                ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
            }`} />
            <span className="text-base font-semibold text-ink-2">{opt.label}</span>
          </button>
        ))}
      </div>

      {hasOwnHome === true && (
        <div className="flex flex-col gap-4 animate-slide-down">

          {/* ── Adres koopwoning ─────────────────────────────────────── */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-4">Adres koopwoning</h3>

            {/* Checkbox: gebruik woonadres */}
            {hasProfileAddress && (
              <button
                type="button"
                onClick={toggleUseProfile}
                className="flex items-center gap-3 w-full text-left mb-4"
              >
                {/* Visuele checkbox */}
                <div className={`h-5 w-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                  useProfile ? 'bg-sage-600 border-sage-600' : 'bg-white border-sand-400'
                }`}>
                  {useProfile && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </div>
                <span className="text-sm font-medium text-ink-2">
                  Gebruik mijn woonadres uit Persoonlijke gegevens
                </span>
              </button>
            )}

            {/* Adres-display: zichtbaar als checkbox aangevinkt EN profiel-adres beschikbaar */}
            {useProfile && hasProfileAddress && (
              <div className="rounded-xl bg-sage-50 border border-sage-200 px-4 py-3 flex items-start gap-3 mb-3">
                <Home className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
                <div>
                  {(displayStreet || displayNumber) && (
                    <p className="text-sm font-semibold text-sage-900">
                      {[displayStreet, displayNumber, displayAddition].filter(Boolean).join(' ')}
                    </p>
                  )}
                  {(displayPostal || displayCity) && (
                    <p className="text-sm text-sage-700">
                      {[displayPostal, displayCity].filter(Boolean).join(' ')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Adresvelden: zichtbaar als checkbox uitgevinkt OF geen profiel-adres */}
            {(!useProfile || !hasProfileAddress) && (
              <div className="flex flex-col gap-3 animate-slide-down">
                {hasProfileAddress && (
                  <p className="text-xs text-ink-4">
                    Vul het adres van uw koopwoning in.
                  </p>
                )}
                {!hasProfileAddress && (
                  <p className="text-xs text-ink-4 leading-relaxed">
                    Vul het adres van uw koopwoning in. U kunt ook eerst uw adres
                    invoeren bij <strong>Persoonlijke gegevens</strong> - dan wordt
                    het hier automatisch overgenomen.
                  </p>
                )}
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <Input label="Straatnaam" value={data.street}
                      onChange={e => updAddr('street', e.target.value)}
                      placeholder="Straatnaam" autoFocus={!hasProfileAddress} />
                  </div>
                  <Input label="Huisnummer" value={data.houseNumber}
                    onChange={e => updAddr('houseNumber', e.target.value)}
                    placeholder="12" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Postcode" value={data.postalCode}
                    onChange={e => updAddr('postalCode', e.target.value)}
                    placeholder="1234 AB" />
                  <Input label="Woonplaats" value={data.city}
                    onChange={e => updAddr('city', e.target.value)}
                    placeholder="Amsterdam" />
                </div>
                <Input label="Toevoeging (optioneel)" value={data.houseNumberAddition}
                  onChange={e => updAddr('houseNumberAddition', e.target.value)}
                  placeholder="Bijv: A, bis"
                  hint="Alleen invullen als het adres een toevoeging heeft" />
              </div>
            )}
          </Card>

          {/* ── Hypotheek ─────────────────────────────────────────────────── */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-4">Hypotheek</h3>
            <div className="flex flex-col gap-3">
              {/* Dropdown hypotheekverstrekker */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink-2">Hypotheekverstrekker</label>
                <select
                  value={data.mortgageProvider || (data.mortgage ? 'anders' : '')}
                  onChange={e => setData(p => ({ ...p, mortgageProvider: e.target.value }))}
                  className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors"
                >
                  <option value="">Geen hypotheek / onbekend</option>
                  {['ABN AMRO','Aegon','Allianz','Argenta','ASN Bank','BLG Wonen',
                    'Florius','Handelsbanken','ING','Lloyds Bank','Munt Hypotheken',
                    'Nationale Nederlanden','Obvion','Rabobank','RegioBank','SNS',
                    'Van Lanschot','Anders'].map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              {/* Vrij veld bij "Anders" */}
              {data.mortgageProvider === 'Anders' && (
                <Input label="Naam hypotheekverstrekker" value={data.mortgageNotes}
                  onChange={e => setData(p => ({ ...p, mortgageNotes: e.target.value }))}
                  placeholder="Bijv: Tulp Hypotheken" />
              )}
              {/* Legacy-veld tonen als er oude data is maar geen nieuwe velden */}
              {data.mortgage && !data.mortgageProvider && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <p className="text-xs text-amber-800">
                    <strong>Eerder ingevuld:</strong> {data.mortgage}
                    <br />Vul het hypotheeknummer hieronder opnieuw in voor een beter overzicht.
                  </p>
                </div>
              )}
              <Input label="Hypotheeknummer / leningnummer"
                value={data.mortgageNumber}
                onChange={e => setData(p => ({ ...p, mortgageNumber: e.target.value }))}
                placeholder="Bijv: HYP-123456789"
                hint="Dit nummer is essentieel voor nabestaanden bij contact met de bank" />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Contactpersoon of afdeling (optioneel)"
                  value={data.mortgageContact}
                  onChange={e => setData(p => ({ ...p, mortgageContact: e.target.value }))}
                  placeholder="Bijv: Hypotheekservice" />
                <Input label="Telefoonnummer (optioneel)" type="tel"
                  value={data.mortgagePhone}
                  onChange={e => setData(p => ({ ...p, mortgagePhone: e.target.value }))}
                  placeholder="0800-..." />
              </div>
              <Input label="E-mailadres (optioneel)" type="email"
                value={data.mortgageEmail}
                onChange={e => setData(p => ({ ...p, mortgageEmail: e.target.value }))}
                placeholder="hypotheek@bank.nl" />
            </div>
          </Card>

          {/* ── Overlijdensrisicoverzekering (ORV) ──────────────────────────── */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-2">Overlijdensrisicoverzekering (ORV)</h3>
            <p className="text-xs text-ink-4 mb-4 leading-relaxed">
              Veel hypotheken zijn gekoppeld aan een ORV. Nabestaanden weten dit vaak niet - terwijl
              deze verzekering de hypotheekschuld (deels) kan aflossen.
            </p>
            <div className="flex flex-col gap-2 mb-3">
              {[
                { value: 'yes',     label: 'Ja, er is een ORV' },
                { value: 'no',      label: 'Nee' },
                { value: 'unknown', label: 'Weet ik niet' },
              ].map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setData(p => ({ ...p, hasOrv: opt.value }))}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    data.hasOrv === opt.value
                      ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
                  }`}>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                    data.hasOrv === opt.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                  }`} />
                  <span className="text-sm text-ink">{opt.label}</span>
                </button>
              ))}
            </div>
            {data.hasOrv === 'yes' && (
              <div className="flex flex-col gap-3 animate-slide-down">
                <div className="grid gap-3 sm:grid-cols-2">
                  <SelectWithOther label="Verzekeraar" options={ORV_INSURERS}
                    value={data.orvInsurer}
                    onChange={v => setData(p => ({ ...p, orvInsurer: v }))}
                    placeholder="Kies verzekeraar..."
                    otherOptionLabel="Anders"
                    otherFieldLabel="Naam verzekeraar"
                    otherPlaceholder="Naam verzekeraar" />
                  <Input label="Polisnummer" value={data.orvPolicyNr}
                    onChange={e => setData(p => ({ ...p, orvPolicyNr: e.target.value }))}
                    placeholder="Bijv: ORV-987654" />
                </div>
                <Input label="Contactgegevens verzekeraar (optioneel)" value={data.orvContact}
                  onChange={e => setData(p => ({ ...p, orvContact: e.target.value }))}
                  placeholder="Bijv: 088-..., schade@nn.nl" />
                <Input label="Bijzonderheden (optioneel)" value={data.orvNotes}
                  onChange={e => setData(p => ({ ...p, orvNotes: e.target.value }))}
                  placeholder="Bijv: loopt tot 2035, alleen bij overlijden voor 70e" />
              </div>
            )}
          </Card>

          {/* ── Overige woninggegevens ────────────────────────────────────── */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Overige gegevens</h3>
            <div className="flex flex-col gap-3">
              <Input label="Eigendomssituatie" value={data.landlord}
                onChange={e => setData(p => ({ ...p, landlord: e.target.value }))}
                placeholder="Bijv: op eigen naam, samen met partner" />
              <Input label="Reservesleutels en toegang" value={data.keys}
                onChange={e => setData(p => ({ ...p, keys: e.target.value }))}
                placeholder="Bijv: bij buurvrouw Van Dam" />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink-2">Documenten aanwezig bij</label>
                <select
                  value={data.docsLocation}
                  onChange={e => setData(p => ({ ...p, docsLocation: e.target.value }))}
                  className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors"
                >
                  <option value="">Selecteer...</option>
                  {['Thuis','Partner','Notaris','Administratie','Digitale kluis','Anders'].map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

        </div>
      )}
    </div>
  )
}
